import React from "react";
import { Link } from "react-router-dom";
import { Link2, ImagePlus, Plus, X, RotateCcw } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { mediaUrl } from "../../lib/api";
import { UploadButton } from "./UploadButton";

const slug = (k) => k.replace(/[^a-zA-Z0-9]+/g, "-");

export function EText({ k, as: Tag = "span", className = "", children, multiline = false }) {
  const { get, editMode, save } = useSite();
  const value = get(k, children);
  const style = multiline ? { whiteSpace: "pre-line" } : undefined;
  if (!editMode) return <Tag className={className} style={style}>{value}</Tag>;
  return (
    <Tag key={value} contentEditable suppressContentEditableWarning className={`${className} cms-editable`} style={style}
      data-testid={`edit-${slug(k)}`} title="Click to edit"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onKeyDown={(e) => { if (!multiline && e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      onBlur={(e) => { const v = e.currentTarget.innerText.trim(); if (v !== String(value).trim()) save(k, v); }}>
      {value}
    </Tag>
  );
}

export function ELink({ k, to, className = "", children, testid }) {
  const { get, editMode, save } = useSite();
  const href = get(`${k}.href`, to);
  const isExt = /^(https?:|mailto:|tel:)/.test(href);
  if (editMode) {
    return (
      <span className={`${className} relative`} data-testid={testid}>
        <EText k={k}>{children}</EText>
        <button type="button" title={`Link: ${href}`} data-testid={`edit-link-${slug(k)}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); const v = window.prompt("Where should this button/link go? (e.g. /packages or https://...)", href); if (v !== null) save(`${k}.href`, v.trim()); }}
          className="cms-chip"><Link2 size={12} /></button>
      </span>
    );
  }
  const text = get(k, children);
  if (isExt) return <a href={href} target="_blank" rel="noreferrer" className={className} data-testid={testid}>{text}</a>;
  return <Link to={href} className={className} data-testid={testid}>{text}</Link>;
}

export function EImg({ k, src, alt = "", className = "", wrapperClassName = "relative" }) {
  const { get, editMode, save, reset } = useSite();
  const url = mediaUrl(get(k, src));
  if (!editMode) return <img src={url} alt={alt} className={className} />;
  return (
    <div className={`${wrapperClassName} cms-img-wrap`}>
      <img src={url} alt={alt} className={className} />
      <ImageControls k={k} save={save} reset={reset} />
    </div>
  );
}

export function ImageControls({ k, save, reset, label = "Replace image", className = "absolute top-3 right-3" }) {
  return (
    <div className={`${className} z-20 flex flex-wrap gap-2`} onClick={(e) => e.stopPropagation()}>
      <UploadButton accept="image/*" isPublic label={label} testid={`upload-${slug(k)}`} onUploaded={(m) => save(k, m.url)} />
      <button type="button" className="cms-chip !static" title="Use an image URL" data-testid={`url-${slug(k)}`}
        onClick={() => { const v = window.prompt("Paste an image URL"); if (v) save(k, v.trim()); }}><ImagePlus size={13} /></button>
      <button type="button" className="cms-chip !static" title="Restore original" onClick={() => reset(k)}><RotateCcw size={13} /></button>
    </div>
  );
}

export function EList({ k, items, render, className = "" }) {
  const { get, editMode, save } = useSite();
  const list = get(k, items);
  if (!editMode) return <ul className={className}>{list.map((it, i) => render(it, i))}</ul>;
  const update = (i, v) => save(k, list.map((x, j) => (j === i ? v : x)));
  return (
    <ul className={className}>
      {list.map((it, i) => render(
        <span className="inline-flex items-start gap-1">
          <span key={it} contentEditable suppressContentEditableWarning className="cms-editable" data-testid={`edit-${slug(k)}-${i}`}
            onBlur={(e) => { const v = e.currentTarget.innerText.trim(); if (v && v !== it) update(i, v); }}>{it}</span>
          <button type="button" className="text-red-400 hover:text-red-600" title="Remove" onClick={() => save(k, list.filter((_, j) => j !== i))}><X size={13} /></button>
        </span>, i))}
      <li>
        <button type="button" className="cms-chip !static mt-1" data-testid={`add-${slug(k)}`}
          onClick={() => { const v = window.prompt("New item text"); if (v) save(k, [...list, v.trim()]); }}><Plus size={12} /> Add item</button>
      </li>
    </ul>
  );
}
